import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import ShareButton from "@/components/ShareButton";
import { Trophy, ArrowLeft, MapPin, Calendar } from "lucide-react";

const METRIC_LABELS = {
  exit_velocity: "Exit Velocity",
  average_exit_velocity: "Avg Exit Velocity",
  bat_speed: "Bat Speed",
  ten_yard_sprint: "10-Yard Sprint",
  thirty_yard_sprint: "30-Yard Sprint",
  sixty_yard_sprint: "60-Yard Sprint",
  five_ten_five_shuttle: "5-10-5 Shuttle",
  infield_velocity: "Infield Velocity",
  outfield_velocity: "Outfield Velocity",
  pop_time: "Pop Time",
  catcher_velocity: "Catcher Velocity",
  fastball_velocity: "Fastball Velocity",
  spin_rate: "Spin Rate",
};

const VERIFICATION_BADGE = {
  cbr_verified: "bg-[#E63946] text-white",
  staff_entered: "bg-[#0A192F] text-white",
  self_reported: "bg-slate-200 text-slate-700",
};

export default function CampResults() {
  const { slug } = useParams();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    api.get(`/camps/${slug}`).then(({ data: camp }) => {
      return api.get(`/camps/${camp.id}/results`).then(({ data }) => {
        setResults(data);
        const metrics = Object.keys(data.results_by_metric || {});
        if (metrics.length > 0) setSelectedMetric(metrics[0]);
      });
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <PublicLayout><div className="max-w-7xl mx-auto p-10 label-overline">Loading results…</div></PublicLayout>;
  if (notFound || !results) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto p-10">
          <div className="font-display text-3xl uppercase">Camp not found</div>
          <Link to="/camps" className="text-[#E63946] text-sm underline mt-3 inline-block">← All camps</Link>
        </div>
      </PublicLayout>
    );
  }

  const camp = results.camp;
  const allMetrics = Object.keys(results.results_by_metric || {});
  const currentEntries = (results.results_by_metric || {})[selectedMetric] || [];

  return (
    <PublicLayout>
      <section className="relative bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url(${camp.cover_image_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <Link to={`/camps/${camp.slug}`} className="inline-flex items-center gap-1 label-overline text-white/60 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to camp
          </Link>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="bg-[#E63946] text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1">Results</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">CBR Verified</span>
          </div>
          <h1 data-testid="camp-results-title" className="font-display text-5xl lg:text-6xl uppercase tracking-tighter mt-3 leading-[0.95]">
            {camp.title}
          </h1>
          <div className="mt-5 flex flex-wrap gap-5 text-sm text-white/80">
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E63946]" />{new Date(camp.start_date).toLocaleDateString("en-CA", { dateStyle: "long" })}</div>
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#E63946]" />{camp.venue}, {camp.city}, {camp.province}</div>
            <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-[#E63946]" />{results.total_entries} verified results</div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <ShareButton title={`${camp.title} — Verified Results`} label="Share Results" />
            <Link to={`/camps/${camp.slug}/awards`} data-testid="camp-results-awards-link" className="inline-flex items-center gap-2 border border-white/30 hover:border-[#E63946] hover:text-[#E63946] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
              <Trophy className="w-4 h-4" /> Camp Awards
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto">
          {allMetrics.length === 0 ? (
            <span className="text-xs text-slate-500 py-2">No verified results recorded yet for this camp.</span>
          ) : allMetrics.map((m) => (
            <button
              key={m}
              data-testid={`results-tab-${m}`}
              onClick={() => setSelectedMetric(m)}
              className={`whitespace-nowrap px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm ${
                selectedMetric === m ? "bg-[#0A192F] text-white" : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"
              }`}
            >
              {METRIC_LABELS[m] || m} <span className="ml-1 text-[10px] opacity-60">({(results.results_by_metric[m] || []).length})</span>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10 min-h-[400px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {allMetrics.length === 0 ? (
            <div className="bg-white border border-slate-200 p-10 text-center">
              <Trophy className="w-12 h-12 mx-auto text-slate-300" />
              <div className="font-display text-2xl uppercase mt-4">Results Pending</div>
              <p className="text-sm text-slate-600 mt-2">Verified metrics are uploaded by CBR staff during and after the camp. Check back soon.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 label-overline w-12">Rank</th>
                    <th className="text-left p-3 label-overline">Player</th>
                    <th className="text-left p-3 label-overline">Class</th>
                    <th className="text-left p-3 label-overline">Pos</th>
                    <th className="text-left p-3 label-overline">Province</th>
                    <th className="text-left p-3 label-overline">Result</th>
                    <th className="text-left p-3 label-overline">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEntries.map((e, i) => {
                    const isPodium = i < 3;
                    return (
                      <tr key={e.id} data-testid={`result-row-${e.id}`} className={`border-b border-slate-100 last:border-0 ${isPodium ? "bg-amber-50/40" : ""}`}>
                        <td className="p-3 font-data text-lg">
                          {i === 0 && <span className="text-[#E63946]">#1</span>}
                          {i === 1 && <span className="text-[#0A192F]">#2</span>}
                          {i === 2 && <span className="text-amber-700">#3</span>}
                          {i > 2 && <span className="text-slate-500">#{i + 1}</span>}
                        </td>
                        <td className="p-3">
                          {e.player?.slug ? (
                            <Link to={`/prospects/${e.player.slug}`} className="font-semibold hover:text-[#E63946]">
                              {e.player.first_name} {e.player.last_name}
                            </Link>
                          ) : (
                            <span className="font-semibold">{e.player?.first_name} {e.player?.last_name}</span>
                          )}
                        </td>
                        <td className="p-3 font-data">{e.player?.graduation_year || "—"}</td>
                        <td className="p-3 font-data">{e.player?.primary_position || "—"}</td>
                        <td className="p-3">{e.player?.province || "—"}</td>
                        <td className="p-3 font-data text-lg text-[#0A192F]">
                          {e.value} <span className="text-xs text-slate-500">{e.units}</span>
                        </td>
                        <td className="p-3">
                          <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${VERIFICATION_BADGE[e.verification_status]}`}>
                            {e.verification_status.replace("_", " ")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Position Groups */}
      {results.position_groups && Object.keys(results.position_groups).length > 0 && (
        <section className="bg-white py-10 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="label-overline text-[#E63946]">Roster</div>
            <h2 className="font-display text-3xl uppercase tracking-tight mt-1">Players by Position</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {Object.entries(results.position_groups).map(([pos, players]) => (
                <div key={pos} data-testid={`position-group-${pos}`} className="border border-slate-200 rounded-sm">
                  <div className="bg-[#0A192F] text-white px-4 py-2 flex items-center justify-between">
                    <span className="font-display uppercase tracking-tight">{pos}</span>
                    <span className="text-[10px] uppercase tracking-wider text-white/60">{players.length} player{players.length !== 1 ? "s" : ""}</span>
                  </div>
                  <ul className="divide-y divide-slate-100">
                    {players.map((pl) => (
                      <li key={pl.id} className="px-4 py-2.5 text-sm flex items-center justify-between">
                        {pl.slug ? (
                          <Link to={`/prospects/${pl.slug}`} className="font-semibold hover:text-[#E63946]">{pl.first_name} {pl.last_name}</Link>
                        ) : (
                          <span className="font-semibold">{pl.first_name} {pl.last_name}</span>
                        )}
                        <span className="font-data text-xs text-slate-500">{pl.graduation_year || "—"} · {pl.province || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
